/**
 * Enriches a drawing point with xIndexFloat from timeMsHint using the timeIndexMap.
 * This ensures all points have index-based coordinates for frame-perfect zoom synchronization.
 *
 * @param point - The drawing point to enrich
 * @param timeIndexMap - The DateTimeIndexMap for resolving time to index
 * @returns Enriched point with xIndexFloat populated if possible
 */
export function enrichPointWithIndex(point, timeIndexMap) {
    // Already has valid xIndexFloat - no enrichment needed
    if (typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat)) {
        return point;
    }
    // Has timeMsHint - try to resolve to index
    if (typeof point.timeMsHint === "number" && Number.isFinite(point.timeMsHint)) {
        const index = timeIndexMap.resolveIndexByTime(point.timeMsHint);
        if (index !== null) {
            return { ...point, xIndexFloat: index };
        }
    }
    // Cannot enrich - return original point
    return point;
}
/**
 * Enriches all points in a drawing object with xIndexFloat from timeMsHint.
 *
 * @param drawing - The drawing object to enrich
 * @param timeIndexMap - The DateTimeIndexMap for resolving time to index
 * @returns New drawing object with enriched points
 */
export function enrichDrawingWithIndex(drawing, timeIndexMap) {
    return {
        ...drawing,
        points: drawing.points.map(point => enrichPointWithIndex(point, timeIndexMap)),
    };
}
/**
 * Enriches an array of drawing objects with xIndexFloat from timeMsHint.
 *
 * @param drawings - Array of drawing objects to enrich
 * @param timeIndexMap - The DateTimeIndexMap for resolving time to index
 * @returns New array with enriched drawings
 */
export function enrichDrawingsWithIndex(drawings, timeIndexMap) {
    return drawings.map(drawing => enrichDrawingWithIndex(drawing, timeIndexMap));
}
